import React, { useState, useEffect, useCallback } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Typography, IconButton, Grid, Paper, Button, Chip, Tooltip,
  Dialog, DialogActions, DialogContent, DialogTitle,
  TextField, Switch, FormControlLabel, CircularProgress,
  Snackbar, Accordion, AccordionSummary, AccordionDetails,
  Table, TableBody, TableCell, TableHead, TableRow,
  Tabs, Tab, Divider, Icon,
  Select, MenuItem, FormControl,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import * as Icons from '@material-ui/icons';
import { useHistory, useParams } from 'react-router-dom';
import { invokeApig } from '../libs/awsLib';
import { useDesignManager } from '../hooks/useDesignManager';
import { useContentManager } from '../hooks/useContentManager';
import { getSectionType, FIELD_TYPES } from '../config/contentTypes';

// ── Estilos ─────────────────────────────────────────────────────────────────

const useStyles = makeStyles((theme) => ({
  root: {
    margin: theme.spacing(3, 0),
    marginBottom: theme.spacing(10),
    width: 'inherit',
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(2),
    gap: theme.spacing(1),
    borderBottom: '1px solid #e0e0e0',
  },
  toolbarTitle: {
    flexGrow: 1,
  },
  paper: {
    padding: theme.spacing(2),
  },
  sectionAccordion: {
    border: '1px solid #e0e0e0',
    marginBottom: theme.spacing(1.5),
    borderRadius: '8px !important',
    '&:before': { display: 'none' },
    '&.Mui-expanded': {
      borderColor: '#7b1fa2',
    },
  },
  sectionSummary: {
    '& .MuiAccordionSummary-content': {
      alignItems: 'center',
      gap: theme.spacing(1),
    },
  },
  listChip: {
    backgroundColor: '#e3f2fd',
    color: '#1565c0',
    fontSize: 11,
    height: 20,
  },
  uniqueChip: {
    backgroundColor: '#fce4ec',
    color: '#880e4f',
    fontSize: 11,
    height: 20,
  },
  tableRow: {
    '&:hover': {
      backgroundColor: '#fafafa',
    },
  },
  itemDialog: {
    '& .MuiDialogContent-root': {
      paddingTop: theme.spacing(2),
    },
  },
  fieldLabel: {
    fontWeight: 600,
    marginBottom: 2,
  },
  richTextArea: {
    width: '100%',
    fontFamily: 'inherit',
    fontSize: 14,
    padding: theme.spacing(1),
    border: '1px solid #c4c4c4',
    borderRadius: 4,
    resize: 'vertical',
    minHeight: 80,
    '&:focus': {
      outline: 'none',
      borderColor: '#7b1fa2',
    },
  },
  noContent: {
    textAlign: 'center',
    padding: theme.spacing(4),
    color: '#9e9e9e',
  },
  pageTabsBar: {
    borderBottom: '1px solid #e0e0e0',
    marginBottom: theme.spacing(2),
  },
  addItemButton: {
    marginBottom: theme.spacing(1),
    borderColor: '#7b1fa2',
    color: '#7b1fa2',
  },
  // ── Card preview grid ──
  cardGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
    gap: theme.spacing(2),
    marginTop: theme.spacing(1),
  },
  cardPreview: {
    position: 'relative',
    border: '1px solid #e0e0e0',
    borderRadius: 8,
    overflow: 'hidden',
    background: '#fff',
    boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
    display: 'flex',
    flexDirection: 'column',
    transition: 'box-shadow 0.2s',
    '&:hover': {
      boxShadow: '0 4px 16px rgba(0,0,0,0.14)',
    },
  },
  cardImagePlaceholder: {
    width: '100%',
    height: 160,
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  cardBody: {
    padding: theme.spacing(1.5),
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  cardMeta: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 6,
    marginTop: 'auto',
    paddingTop: 6,
  },
  cardBadge: {
    backgroundColor: '#ff5722',
    color: '#fff',
    fontSize: 10,
    height: 20,
    fontWeight: 700,
  },
  cardFooter: {
    borderTop: '1px solid #f0f0f0',
    padding: '8px 12px',
    textAlign: 'center',
    flexShrink: 0,
  },
  cardOverlay: {
    position: 'absolute',
    top: 6,
    right: 6,
    display: 'flex',
    gap: 2,
    background: 'rgba(255,255,255,0.92)',
    borderRadius: 4,
    padding: 2,
    boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
  },
  cardIndexBadge: {
    position: 'absolute',
    top: 6,
    left: 6,
    background: 'rgba(0,0,0,0.45)',
    color: '#fff',
    borderRadius: 4,
    padding: '1px 6px',
    fontSize: 10,
    fontWeight: 600,
    lineHeight: '18px',
  },

  // ── Vista previa WYSIWYG ──
  previewCanvas: {
    backgroundColor: '#f5f5f5',
    borderRadius:    8,
    border:          '1px solid #e0e0e0',
    minHeight:       320,
    overflow:        'hidden',
  },
  previewSection: {
    position: 'relative',
    '&:hover $previewOverlay': { opacity: 1 },
  },
  previewOverlay: {
    position:   'absolute',
    top:        10,
    right:      10,
    opacity:    0,
    transition: 'opacity 0.2s',
    display:    'flex',
    gap:        4,
    zIndex:     2,
  },
  modeToggleBtn: {
    borderRadius:  20,
    padding:       '4px 16px',
    textTransform: 'none',
    fontWeight:    600,
  },

  // ── Panel de apariencia ──
  stylePanel: {
    backgroundColor: '#f8f4ff',
    border:          '1px solid #ce93d8',
    borderRadius:    8,
    padding:         theme.spacing(2),
    marginTop:       theme.spacing(1.5),
  },
  stylePanelTitle: {
    color:       '#7b1fa2',
    fontWeight:  600,
    marginBottom: theme.spacing(1.5),
    display:     'flex',
    alignItems:  'center',
    gap:         6,
  },
  styleRow: {
    marginBottom: theme.spacing(1.5),
  },
  colorInputRow: {
    display:    'flex',
    alignItems: 'center',
    gap:        8,
    marginTop:  4,
  },
  styleBtn: {
    minWidth: 0,
    padding:  '2px 10px',
    fontSize: 12,
  },
  styleBtnGroup: {
    display:  'flex',
    gap:      6,
    flexWrap: 'wrap',
    marginTop: 4,
  },
}));

// Detecta si un valor es URL de imagen o texto libre
const isImageUrl = (val) => !!val && (
  val.startsWith('http://') || val.startsWith('https://') ||
  val.startsWith('data:') || val.startsWith('/')
);

// ── Constantes de estilos visuales ──────────────────────────────────────────

const SHADOW_CSS = {
  none: 'none',
  sm:   '0 2px 8px rgba(0,0,0,0.07)',
  md:   '0 4px 16px rgba(0,0,0,0.14)',
  lg:   '0 8px 32px rgba(0,0,0,0.22)',
};
const HOVER_EFFECT = {
  none:  {},
  lift:  { transform: 'translateY(-4px)', boxShadow: '0 8px 24px rgba(0,0,0,0.18)' },
  scale: { transform: 'scale(1.03)',      boxShadow: '0 6px 20px rgba(0,0,0,0.15)' },
  glow:  { boxShadow: '0 0 24px rgba(156,39,176,0.35)' },
};
const PADDING_PX      = { none: 0, sm: 16, md: 40, lg: 72 };
const CONTAINER_MAX_W = { full: '100%', wide: 1200, medium: 900, narrow: 600 };
const MIN_HEIGHT_PX   = { auto: undefined, sm: 200, md: 350, lg: 500, screen: '100vh' };

const GRADIENTS = [
  'linear-gradient(135deg,#667eea 0%,#764ba2 100%)',
  'linear-gradient(135deg,#f093fb 0%,#f5576c 100%)',
  'linear-gradient(135deg,#4facfe 0%,#00f2fe 100%)',
  'linear-gradient(135deg,#43e97b 0%,#38f9d7 100%)',
  'linear-gradient(135deg,#fa709a 0%,#fee140 100%)',
  'linear-gradient(135deg,#a8edea 0%,#fed6e3 100%)',
];

// ── Renderizar campo según tipo ──────────────────────────────────────────────

function FieldInput({ field, value, onChange, isContainer }) {
  switch (field.type) {
    case 'richtext':
      return (
        <textarea
          style={{
            width: '100%', fontFamily: 'inherit', fontSize: 14,
            padding: '8px', border: '1px solid #c4c4c4', borderRadius: 4,
            resize: 'vertical', minHeight: 80, boxSizing: 'border-box',
          }}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={`Ingresa ${field.label.toLowerCase()}...`}
        />
      );
    case 'boolean':
      return (
        <FormControlLabel
          control={
            <Switch
              checked={!!value}
              onChange={(e) => onChange(e.target.checked)}
              color="primary"
            />
          }
          label={value ? 'Sí' : 'No'}
        />
      );
    case 'date':
      return (
        <TextField
          type="date"
          fullWidth
          size="small"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
      );
    case 'number':
      return (
        <TextField
          type="number"
          fullWidth
          size="small"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={`Ingresa ${field.label.toLowerCase()}...`}
        />
      );
    case 'image':
      return (
        <TextField
          fullWidth
          size="small"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={isContainer ? 'https://... o texto para mostrar sobre el fondo' : 'https://ejemplo.com/imagen.jpg'}
          helperText={isContainer ? 'URL de imagen o texto libre (se muestra sobre gradiente)' : 'URL de la imagen'}
          InputProps={{
            endAdornment: value ? (
              isImageUrl(value) ? (
                <img
                  src={value}
                  alt="preview"
                  style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 2, marginLeft: 4 }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ) : (
                <span style={{ fontSize: 10, color: '#9c27b0', marginLeft: 4, fontWeight: 600, whiteSpace: 'nowrap' }}>Texto</span>
              )
            ) : null,
          }}
        />
      );
    case 'url':
      return (
        <TextField
          fullWidth
          size="small"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://..."
          helperText="URL completa incluyendo https://"
        />
      );
    default:
      return (
        <TextField
          fullWidth
          size="small"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={`Ingresa ${field.label.toLowerCase()}...`}
        />
      );
  }
}

// ── Componente principal ─────────────────────────────────────────────────────

function ContentManagerPage() {
  const { designId } = useParams();
  const history = useHistory();
  const classes = useStyles();
  const { loadDesign, listDesigns, saveDesign, isListing } = useDesignManager(designId);
  const { loadContent, saveContent, isSaving, isLoading: isLoadingContent } = useContentManager();

  // Estado principal
  const [designName, setDesignName]             = useState('');
  const [pages, setPages]                       = useState([]);
  const [currentPageId, setCurrentPageId]       = useState(null);
  const [currentPageTitle, setCurrentPageTitle] = useState('');
  const [sections, setSections]                 = useState([]);
  const [content, setContent]                   = useState({});
  const [tabIndex, setTabIndex]                 = useState(0);
  const [isLoadingInfo, setIsLoadingInfo]       = useState(true);
  const [isLoadingPage, setIsLoadingPage]       = useState(false);

  // Toast
  const [openToast, setOpenToast]         = useState(false);
  const [toastMsg, setToastMsg]           = useState('');
  const [toastSeverity, setToastSeverity] = useState('success');

  // Vista previa y estilos
  const [previewMode, setPreviewMode]           = useState(false);
  const [sectionStyles, setSectionStyles]       = useState({});
  const [openStylesFor, setOpenStylesFor]       = useState(null);
  const [pageStyle, setPageStyle]               = useState({ backgroundColor: '', backgroundImage: '' });
  const [openPageStyle, setOpenPageStyle]       = useState(false);

  // Dialog item (agregar/editar item de lista)
  const [openItemDialog, setOpenItemDialog]     = useState(false);
  const [itemDialogSection, setItemDialogSection] = useState(null);
  const [itemDialogData, setItemDialogData]     = useState({});
  const [itemDialogIndex, setItemDialogIndex]   = useState(null); // null = nuevo
  const [itemDialogError, setItemDialogError]   = useState('');

  // Selector de página de detalle dentro del diálogo
  const [detailPageOptions, setDetailPageOptions]       = useState([]);
  const [selectedDetailPageId, setSelectedDetailPageId] = useState('');
  const [detailItems, setDetailItems]                   = useState([]);
  const [loadingDetail, setLoadingDetail]               = useState(false);

  // ── Carga inicial ────────────────────────────────────────────────────────

  useEffect(() => {
    loadInitialData();
  }, [designId]); // eslint-disable-line

  const loadInitialData = async () => {
    setIsLoadingInfo(true);
    try {
      const designRes = await invokeApig({
        path:   '/design/find',
        method: 'POST',
        body:   { designId },
      });
      if (designRes.Status === 0 || designRes.statusCode === 200) {
        setDesignName(designRes.design?.name || '');
      }

      const listRes = await listDesigns();
      if (listRes.success) {
        const guidedPages = (listRes.pages || []).filter(
          (p) => p.pageType === 'guided'
        );
        setPages(guidedPages);
        setDetailPageOptions(guidedPages.map((p) => ({
          pageId: p.pageId,
          title:  p.title || p.pageId,
          slug:   p.slug  || '',
        })));
        if (guidedPages.length > 0) {
          await loadPageData(guidedPages[0].pageId);
        }
      }
    } catch (e) {
      showToast('Error al cargar el diseño', 'error');
    } finally {
      setIsLoadingInfo(false);
    }
  };

  const loadPageData = useCallback(async (pageId) => {
    setIsLoadingPage(true);
    try {
      const [designResult, contentResult] = await Promise.all([
        loadDesign(pageId),
        loadContent(designId, pageId),
      ]);

      if (designResult.success) {
        setCurrentPageId(designResult.pageId);
        setCurrentPageTitle(designResult.pageInfo?.title || '');
        const pageSections = designResult.metadata?.guidedSections || [];
        const sorted = [...pageSections].sort((a, b) => {
          const ay = a.layout?.y ?? a.order ?? 0;
          const by = b.layout?.y ?? b.order ?? 0;
          if (ay !== by) return ay - by;
          return (a.layout?.x ?? 0) - (b.layout?.x ?? 0);
        });
        setSections(sorted);
        const styles = {};
        sorted.forEach((s) => { if (s.layout?.cardStyle) styles[s.id] = { ...s.layout.cardStyle }; });
        setSectionStyles(styles);
        setPageStyle(designResult.metadata?.pageStyle || { backgroundColor: '', backgroundImage: '' });
      }

      if (contentResult.success) {
        setContent(contentResult.content || {});
      } else {
        setContent({});
      }
    } finally {
      setIsLoadingPage(false);
    }
  }, [loadDesign, loadContent, designId]);

  const handleTabChange = async (_, newIndex) => {
    setTabIndex(newIndex);
    await loadPageData(pages[newIndex].pageId);
  };

  // ── Guardar ──────────────────────────────────────────────────────────────

  const handleSave = async () => {
    const sectionsWithStyles = sections.map((s) => ({
      ...s,
      layout: { ...s.layout, cardStyle: sectionStyles[s.id] || {} },
    }));
    const [contentResult, designResult] = await Promise.all([
      saveContent(designId, currentPageId, content),
      saveDesign(
        { mode: 'guided' },
        { mode: 'guided', guidedSections: sectionsWithStyles, pageStyle },
        currentPageId,
        currentPageTitle,
        'guided',
      ),
    ]);
    if (contentResult.success && designResult.success) {
      showToast('Contenido guardado correctamente');
    } else {
      showToast('Error al guardar', 'error');
    }
  };

  // ── Contenido de secciones únicas ────────────────────────────────────────

  const handleUniqueFieldChange = (sectionId, fieldKey, value) => {
    setContent((prev) => ({
      ...prev,
      [sectionId]: {
        ...(prev[sectionId] || {}),
        [fieldKey]: value,
      },
    }));
  };

  // ── Items de listas ──────────────────────────────────────────────────────

  const loadDetailItemsForPage = async (detailPageId) => {
    if (!detailPageId) return;
    setLoadingDetail(true);
    try {
      const result = await loadContent(designId, detailPageId);
      const c = result.content || {};
      const all = Object.values(c).flat().filter((it) => it && it.slug);
      setDetailItems(all);
    } catch {
      setDetailItems([]);
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleOpenAddItem = (section) => {
    setItemDialogSection(section);
    setItemDialogIndex(null);
    setItemDialogData({});
    setItemDialogError('');
    setDetailItems([]);
    setSelectedDetailPageId('');
    // Auto-seleccionar si solo hay una página de detalle distinta a la actual
    const sType = getSectionType(section.type);
    if (sType?.canBelist) {
      const available = detailPageOptions.filter((p) => p.pageId !== currentPageId);
      if (available.length === 1) {
        setSelectedDetailPageId(available[0].pageId);
        loadDetailItemsForPage(available[0].pageId);
      }
    }
    setOpenItemDialog(true);
  };

  const handleOpenEditItem = (section, index) => {
    const items = content[section.id] || [];
    const item = items[index];
    setItemDialogSection(section);
    setItemDialogIndex(index);
    setItemDialogData({ ...item });
    setItemDialogError('');
    setDetailItems([]);
    setSelectedDetailPageId('');
    // Detectar página de detalle desde la URL guardada del ítem
    if (item?.url) {
      const parts = item.url.split('/').filter(Boolean);
      if (parts.length >= 2) {
        const found = detailPageOptions.find((p) => p.slug === parts[0]);
        if (found) {
          setSelectedDetailPageId(found.pageId);
          loadDetailItemsForPage(found.pageId);
        }
      }
    }
    setOpenItemDialog(true);
  };

  const handleDeleteItem = (sectionId, index) => {
    setContent((prev) => {
      const items = [...(prev[sectionId] || [])];
      items.splice(index, 1);
      return { ...prev, [sectionId]: items };
    });
  };

  const handleMoveItem = (sectionId, index, direction) => {
    setContent((prev) => {
      const items = [...(prev[sectionId] || [])];
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= items.length) return prev;
      [items[index], items[targetIndex]] = [items[targetIndex], items[index]];
      return { ...prev, [sectionId]: items };
    });
  };

  const handleConfirmItem = () => {
    const requiredFields = (itemDialogSection?.fields || []).filter((f) => f.required);
    const missing = requiredFields.filter((f) => !itemDialogData[f.key]);
    if (missing.length > 0) {
      setItemDialogError(`Campos requeridos: ${missing.map((f) => f.label).join(', ')}`);
      return;
    }
    const finalData = { ...itemDialogData };
    if (selectedDetailPageId && finalData.slug) {
      const detailSlug = detailPageOptions.find((p) => p.pageId === selectedDetailPageId)?.slug || '';
      if (detailSlug) finalData.url = `/${detailSlug}/${finalData.slug}`;
    }
    setContent((prev) => {
      const items = [...(prev[itemDialogSection.id] || [])];
      if (itemDialogIndex === null) {
        items.push({ ...finalData });
      } else {
        items[itemDialogIndex] = { ...finalData };
      }
      return { ...prev, [itemDialogSection.id]: items };
    });
    setOpenItemDialog(false);
  };

  // ── Toast ────────────────────────────────────────────────────────────────

  const showToast = (msg, severity = 'success') => {
    setToastMsg(msg);
    setToastSeverity(severity);
    setOpenToast(true);
  };

  // ── Helpers de estilos ──────────────────────────────────────────────────

  const getSectionBorderCSS = (layout) => {
    if (!layout?.borderStyle || layout.borderStyle === 'none') return {};
    const bw = `${layout.borderWidth || 1}px solid ${layout.borderColor || '#e0e0e0'}`;
    if (layout.borderStyle === 'all')    return { border: bw };
    if (layout.borderStyle === 'top')    return { borderTop: bw };
    if (layout.borderStyle === 'bottom') return { borderBottom: bw };
    if (layout.borderStyle === 'tb')     return { borderTop: bw, borderBottom: bw };
    return {};
  };

  const getSectionWrapperStyle = (section) => {
    const layout = section.layout || {};
    return {
      backgroundColor: layout.backgroundColor || 'transparent',
      paddingTop:      PADDING_PX[layout.paddingTop]    ?? 40,
      paddingBottom:   PADDING_PX[layout.paddingBottom] ?? 40,
      minHeight:       MIN_HEIGHT_PX[layout.minHeight || 'auto'],
      borderRadius:    layout.borderRadius ? `${layout.borderRadius}px` : 0,
      ...getSectionBorderCSS(layout),
    };
  };

  const getSectionInnerStyle = (section) => {
    const layout = section.layout || {};
    return {
      maxWidth:  CONTAINER_MAX_W[layout.containerWidth || 'wide'] ?? 1200,
      margin:    '0 auto',
      padding:   '0 24px',
      textAlign: layout.alignment || 'center',
      color:     layout.textColor || 'inherit',
    };
  };

  // ── Panel de apariencia de cards ────────────────────────────────────────

  const updateSectionStyle = (sectionId, key, value) => {
    setSectionStyles((prev) => ({
      ...prev,
      [sectionId]: { ...(prev[sectionId] || {}), [key]: value },
    }));
  };

  const renderStylePanel = (section) => {
    const cs = sectionStyles[section.id] || {};
    const SHADOWS    = [{ v: 'none', l: 'Sin sombra' }, { v: 'sm', l: 'Suave' }, { v: 'md', l: 'Media' }, { v: 'lg', l: 'Profunda' }];
    const EFFECTS    = [{ v: 'none', l: 'Ninguno' }, { v: 'lift', l: 'Elevar' }, { v: 'scale', l: 'Escalar' }, { v: 'glow', l: 'Brillo' }];
    const RADII      = [{ v: 0, l: 'Sin redondeo' }, { v: 4, l: 'Suave' }, { v: 8, l: 'Medio' }, { v: 16, l: 'Grande' }];
    const IMG_HEIGHTS = [{ v: 120, l: '120px' }, { v: 160, l: '160px' }, { v: 200, l: '200px' }, { v: 240, l: '240px' }];

    const btnGroup = (label, key, options, currentVal) => (
      <div className={classes.styleRow}>
        <Typography variant="caption" display="block" style={{ color: '#757575', marginBottom: 4 }}>{label}</Typography>
        <div className={classes.styleBtnGroup}>
          {options.map((o) => (
            <Button key={o.v} size="small" className={classes.styleBtn}
              variant={(cs[key] ?? currentVal) === o.v ? 'contained' : 'outlined'}
              color={(cs[key] ?? currentVal) === o.v ? 'primary' : 'default'}
              onClick={() => updateSectionStyle(section.id, key, o.v)}
            >{o.l}</Button>
          ))}
        </div>
      </div>
    );

    return (
      <div className={classes.stylePanel}>
        <Typography variant="caption" className={classes.stylePanelTitle}>
          <Icon style={{ fontSize: 16 }}>palette</Icon>
          Apariencia de Cards
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            {btnGroup('Sombra', 'cardShadow', SHADOWS, 'sm')}
          </Grid>
          <Grid item xs={12} sm={6}>
            {btnGroup('Efecto al pasar cursor', 'hoverEffect', EFFECTS, 'none')}
          </Grid>
          <Grid item xs={12} sm={6}>
            {btnGroup('Redondeo del card', 'cardRadius', RADII, 8)}
          </Grid>
          {section.fields?.some((f) => f.type === 'image') && (
            <Grid item xs={12} sm={6}>
              {btnGroup('Alto de imagen', 'imageHeight', IMG_HEIGHTS, 160)}
            </Grid>
          )}
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" display="block" style={{ color: '#757575', marginBottom: 4 }}>Color de fondo del card</Typography>
            <div className={classes.colorInputRow}>
              <input
                type="color"
                value={cs.cardBg || '#ffffff'}
                onChange={(e) => updateSectionStyle(section.id, 'cardBg', e.target.value)}
                style={{ width: 36, height: 36, border: '1px solid #e0e0e0', cursor: 'pointer', borderRadius: 4, padding: 2 }}
              />
              <TextField size="small" value={cs.cardBg || ''}
                onChange={(e) => updateSectionStyle(section.id, 'cardBg', e.target.value)}
                placeholder="#ffffff"
                inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }}
                style={{ flex: 1 }}
              />
              {cs.cardBg && (
                <IconButton size="small" onClick={() => updateSectionStyle(section.id, 'cardBg', '')} style={{ color: '#9e9e9e' }}>
                  <Icons.Close fontSize="small" />
                </IconButton>
              )}
            </div>
          </Grid>
        </Grid>
      </div>
    );
  };

  // ── Vista previa WYSIWYG con contenido real ──────────────────────────────

  const renderPreviewCard = (section, item, index) => {
    const cs        = sectionStyles[section.id] || {};
    const fields    = section.fields || [];
    const layout    = section.layout || {};
    const imageField   = fields.find((f) => f.type === 'image');
    const titleField   = fields.find((f) => ['titulo', 'nombre'].includes(f.key)) || fields.find((f) => f.type === 'text' && f.required) || fields.find((f) => f.type === 'text');
    const descField    = fields.find((f) => ['descripcion', 'resumen', 'texto', 'respuesta'].includes(f.key) && f !== titleField) || fields.find((f) => (f.type === 'richtext' || f.type === 'text') && f !== titleField);
    const badgeField   = fields.find((f) => ['badge', 'categoria', 'precio'].includes(f.key));
    const urlField     = fields.find((f) => f.type === 'url');
    const btnTextField = fields.find((f) => f.key === 'texto_boton');

    const imgSrc   = imageField   ? item[imageField.key]   : null;
    const titleVal = titleField   ? item[titleField.key]   : null;
    const descVal  = descField    ? item[descField.key]    : null;
    const badgeVal = badgeField   ? item[badgeField.key]   : null;
    const btnLabel = (btnTextField && item[btnTextField.key]) || 'Ver más';
    const imgH     = cs.imageHeight || 160;
    const radius   = cs.cardRadius  ?? 8;
    const shadow   = SHADOW_CSS[cs.cardShadow || 'sm'];
    const cardBg   = cs.cardBg || '#fff';
    const hoverEff = cs.hoverEffect || 'none';
    const tc       = layout.textColor || '#333';

    return (
      <div
        key={index}
        style={{
          backgroundColor: cardBg,
          boxShadow:       shadow,
          borderRadius:    radius,
          overflow:        'hidden',
          display:         'flex',
          flexDirection:   'column',
          transition:      'all 0.22s ease',
        }}
        onMouseEnter={(e) => {
          const eff = HOVER_EFFECT[hoverEff] || {};
          if (eff.transform)  e.currentTarget.style.transform  = eff.transform;
          if (eff.boxShadow)  e.currentTarget.style.boxShadow  = eff.boxShadow;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform  = '';
          e.currentTarget.style.boxShadow  = shadow;
        }}
      >
        {imageField && (
          isImageUrl(imgSrc) ? (
            <img src={imgSrc} alt={titleVal || ''} style={{ width: '100%', height: imgH, objectFit: 'cover', display: 'block' }}
              onError={(e) => { e.target.style.display = 'none'; }} />
          ) : (
            <div style={{ height: imgH, background: GRADIENTS[index % GRADIENTS.length], display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {imgSrc
                ? <Typography style={{ color: '#fff', fontWeight: 600, textAlign: 'center', padding: '0 12px', lineHeight: 1.3 }}>{imgSrc}</Typography>
                : <Icons.Image style={{ fontSize: 32, color: 'rgba(255,255,255,0.4)' }} />}
            </div>
          )
        )}
        <div style={{ padding: '14px 16px', flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          {titleVal && <Typography style={{ fontWeight: 700, fontSize: 15, marginBottom: 6, color: tc }}>{titleVal}</Typography>}
          {descVal  && <Typography variant="body2" style={{ color: tc, opacity: 0.7, lineHeight: 1.5, flexGrow: 1 }}>
            {String(descVal).substring(0, 150)}{String(descVal).length > 150 ? '…' : ''}
          </Typography>}
          {badgeVal && <Chip label={badgeVal} size="small" style={{ backgroundColor: '#ff5722', color: '#fff', fontSize: 10, height: 20, fontWeight: 700, marginTop: 8, alignSelf: 'flex-start' }} />}
        </div>
        {urlField && <div style={{ borderTop: '1px solid #f0f0f0', padding: '8px 16px', textAlign: 'center' }}>
          <Typography variant="caption" style={{ color: '#7b1fa2', fontWeight: 700 }}>{btnLabel} →</Typography>
        </div>}
      </div>
    );
  };

  const renderPreviewSectionContent = (section) => {
    const layout    = section.layout || {};
    const tc        = layout.textColor || '#333';
    const fields    = section.fields || [];
    const items     = content[section.id] || [];
    const sType     = getSectionType(section.type);
    const display   = layout.display || 'grid_3';
    const colsMap   = { grid_2: 2, grid_3: 3, grid_4: 4, list: 1, carousel: 3, single: 1 };
    const cols      = colsMap[display] || 3;
    const titleSt   = { color: tc, fontWeight: 700, marginBottom: 16 };
    const bodySt    = { color: tc, opacity: 0.7 };

    if (section.isList) {
      return (
        <div>
          <Typography variant="h5" style={{ ...titleSt, textAlign: 'inherit', marginBottom: 24 }}>{section.label}</Typography>
          {items.length === 0 ? (
            <Typography variant="body2" style={{ ...bodySt, textAlign: 'center', padding: 24 }}>Sin elementos en esta sección</Typography>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 16, textAlign: 'left' }}>
              {items.map((item, i) => renderPreviewCard(section, item, i))}
            </div>
          )}
        </div>
      );
    }

    const sectionData = content[section.id] || {};
    const titleField = fields.find((f) => ['titulo', 'nombre'].includes(f.key)) || fields.find((f) => f.type === 'text' && f.required) || fields.find((f) => f.type === 'text');
    const descField  = fields.find((f) => f !== titleField && (f.type === 'richtext' || f.type === 'text'));
    const imgField   = fields.find((f) => f.type === 'image');
    const urlField   = fields.find((f) => f.type === 'url');

    if (sType?.value === 'hero_banner') {
      return (
        <div style={{ padding: '32px 0' }}>
          {imgField && isImageUrl(sectionData[imgField.key]) && (
            <img src={sectionData[imgField.key]} alt="" style={{ width: '100%', maxHeight: 300, objectFit: 'cover', borderRadius: 8, marginBottom: 24 }} />
          )}
          <Typography variant="h3" style={{ ...titleSt, fontSize: 42 }}>{sectionData[titleField?.key] || section.label}</Typography>
          {descField && <Typography variant="h6" style={{ ...bodySt, marginBottom: 24 }}>{sectionData[descField.key] || 'Subtítulo descriptivo'}</Typography>}
          {urlField && <Button variant="contained" color="primary" size="large">{fields.find((f) => f.key === 'texto_boton') ? sectionData[fields.find((f) => f.key === 'texto_boton').key] || 'Ver más' : 'Ver más'}</Button>}
        </div>
      );
    }

    if (sType?.isTypography) {
      const sizes = { heading_h1: 48, heading_h2: 36, heading_h3: 28, paragraph_text: 16, text_label: 13 };
      return <Typography style={{ color: tc, fontSize: sizes[section.type] || 16, fontWeight: section.type.includes('heading') ? 700 : 400 }}>{sectionData[titleField?.key] || section.label}</Typography>;
    }

    if (sType?.value === 'search_bar') {
      return (
        <div>
          <Typography variant="h5" style={{ ...titleSt, marginBottom: 16 }}>{section.label}</Typography>
          <TextField placeholder={sectionData[fields.find((f) => f.key === 'placeholder')?.key] || 'Buscar...'} fullWidth variant="outlined"
            InputProps={{ startAdornment: <Icons.Search style={{ color: '#9c27b0', marginRight: 8 }} />, readOnly: true }}
          />
        </div>
      );
    }

    if (sType?.value === 'contact_form') {
      return (
        <Grid container justifyContent="center">
          <Grid item xs={12} sm={8}>
            <Typography variant="h5" style={{ ...titleSt, marginBottom: 24 }}>{section.label}</Typography>
            {fields.slice(0, 3).map((f) => (
              <TextField key={f.key} label={f.label} fullWidth variant="outlined" size="small"
                value={sectionData[f.key] || ''} style={{ marginBottom: 12 }} InputProps={{ readOnly: true }} />
            ))}
            <Button variant="contained" color="primary" fullWidth>Enviar</Button>
          </Grid>
        </Grid>
      );
    }

    if (sType?.value === 'footer') {
      return (
        <Grid container spacing={3} style={{ textAlign: 'left' }}>
          <Grid item xs={12} sm={4}>
            <Typography variant="h6" style={titleSt}>{section.label}</Typography>
            {fields.slice(0, 3).map((f) => (
              <Typography key={f.key} variant="body2" style={{ ...bodySt, marginBottom: 4 }}>
                {sectionData[f.key] || f.label}
              </Typography>
            ))}
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="h6" style={titleSt}>Navegación</Typography>
            {['Inicio', 'Servicios', 'Contacto'].map((t) => (
              <Typography key={t} variant="body2" style={{ ...bodySt, marginBottom: 4 }}>{t}</Typography>
            ))}
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="body2" style={bodySt}>© 2024 Tu Empresa. Todos los derechos reservados.</Typography>
          </Grid>
        </Grid>
      );
    }

    return (
      <div>
        <Typography variant="h5" style={{ ...titleSt, marginBottom: 20 }}>{sectionData[titleField?.key] || section.label}</Typography>
        {descField && <Typography variant="body1" style={bodySt}>{sectionData[descField.key] || ''}</Typography>}
        {fields.filter((f) => f !== titleField && f !== descField && f !== imgField).slice(0, 3).map((f) => (
          <Typography key={f.key} variant="body2" style={{ ...bodySt, marginTop: 4 }}>
            <strong>{f.label}:</strong> {sectionData[f.key] || '—'}
          </Typography>
        ))}
      </div>
    );
  };

  const renderPreviewCanvas = () => {
    if (sections.length === 0) {
      return (
        <div className={classes.previewCanvas} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 240 }}>
          <div style={{ textAlign: 'center', color: '#bdbdbd' }}>
            <Icons.Visibility style={{ fontSize: 64, opacity: 0.25 }} />
            <Typography variant="body1" style={{ marginTop: 8 }}>No hay secciones configuradas.</Typography>
          </div>
        </div>
      );
    }
    return (
      <div className={classes.previewCanvas}>
        <div style={{
          backgroundColor: pageStyle.backgroundColor || '#ffffff',
          backgroundImage: pageStyle.backgroundImage ? `url(${pageStyle.backgroundImage})` : undefined,
          backgroundSize: 'cover', backgroundPosition: 'center',
          minHeight: 400,
        }}>
          {sections.map((section) => (
            <div key={section.id} className={classes.previewSection}>
              <div style={{ position: 'relative', overflow: 'hidden', ...getSectionWrapperStyle(section) }}>
                <div style={getSectionInnerStyle(section)}>
                  {renderPreviewSectionContent(section)}
                </div>
              </div>
              {section.isList && (
                <div className={classes.previewOverlay}>
                  <Tooltip title="Apariencia de cards" enterDelay={200}>
                    <IconButton size="small"
                      onClick={() => setOpenStylesFor((prev) => prev === section.id ? null : section.id)}
                      style={{ backgroundColor: 'rgba(255,255,255,0.92)', padding: 4 }}>
                      <Icons.Palette style={{ fontSize: 14, color: '#7b1fa2' }} />
                    </IconButton>
                  </Tooltip>
                </div>
              )}
              {section.isList && openStylesFor === section.id && (
                <div style={{ padding: '0 24px 16px', backgroundColor: '#fff' }}>
                  {renderStylePanel(section)}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // ── Render sección única ─────────────────────────────────────────────────

  const renderUniqueSection = (section) => {
    const sectionContent = content[section.id] || {};
    return (
      <Grid container spacing={2} style={{ marginTop: 4 }}>
        {section.fields.map((field) => (
          <Grid item xs={12} sm={field.type === 'richtext' ? 12 : 6} key={field.key}>
            <Typography variant="caption" className={classes.fieldLabel} display="block">
              {field.label}
              {field.required && <span style={{ color: '#f44336', marginLeft: 2 }}>*</span>}
              <span style={{ color: '#9e9e9e', marginLeft: 4, fontWeight: 400 }}>
                ({FIELD_TYPES.find((ft) => ft.value === field.type)?.label || field.type})
              </span>
            </Typography>
            <FieldInput
              field={field}
              value={sectionContent[field.key]}
              onChange={(val) => handleUniqueFieldChange(section.id, field.key, val)}
            />
          </Grid>
        ))}
      </Grid>
    );
  };

  // ── Render sección con cards visuales (para containers con cardPreview) ─────

  const renderCardPreviewSection = (section, sType) => {
    const items     = content[section.id] || [];
    const fields    = section.fields || [];

    // Mapeo de campos por clave conocida, con fallback por tipo
    const imageField   = fields.find((f) => f.type === 'image');
    const titleField   = fields.find((f) => ['titulo', 'nombre'].includes(f.key))
                      || fields.find((f) => f.type === 'text' && f.required)
                      || fields.find((f) => f.type === 'text');
    const descField    = fields.find((f) => ['descripcion', 'resumen', 'texto', 'respuesta'].includes(f.key) && f !== titleField)
                      || fields.find((f) => (f.type === 'richtext' || f.type === 'text') && f !== titleField);
    const badgeField   = fields.find((f) => ['badge', 'categoria', 'precio'].includes(f.key));
    const personField  = fields.find((f) => ['persona', 'autor', 'canalizador', 'cargo'].includes(f.key));
    const urlField     = fields.find((f) => f.type === 'url');
    const btnTextField = fields.find((f) => f.key === 'texto_boton');

    // Gradientes de fallback por índice
    const gradients = [
      'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
      'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
      'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
    ];

    // Columnas según el display configurado en el diseñador
    const display = section.layout?.display || 'grid_3';
    const colsMap = { grid_2: 2, grid_3: 3, grid_4: 4, list: 1, carousel: 3, single: 1 };
    const cols    = colsMap[display] || 3;
    const gridCols = `repeat(${cols}, 1fr)`;

    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <Typography variant="caption" style={{ color: '#9e9e9e' }}>
            {display === 'list' ? 'Lista vertical' : display === 'carousel' ? 'Carrusel' : `Grid ${cols} columnas`}
            {' · '}{items.length} elemento{items.length !== 1 ? 's' : ''}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Icons.Add />}
            className={classes.addItemButton}
            onClick={() => handleOpenAddItem(section)}
          >
            Agregar card
          </Button>
        </div>

        {items.length === 0 ? (
          <div className={classes.noContent}>
            <Icons.ViewModule style={{ fontSize: 48, opacity: 0.25 }} />
            <Typography variant="body2" style={{ marginTop: 8 }}>Sin cards. Agrega el primero.</Typography>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: gridCols, gap: 16 }}>
            {items.map((item, index) => {
              const imgSrc    = imageField ? item[imageField.key] : null;
              const titleVal  = titleField ? item[titleField.key] : null;
              const descVal   = descField  ? item[descField.key]  : null;
              const badgeVal  = badgeField ? item[badgeField.key] : null;
              const personVal = personField ? item[personField.key] : null;
              const btnLabel  = (btnTextField && item[btnTextField.key]) || 'Ver más';

              return (
                <div key={index} className={classes.cardPreview}>
                  {/* Imagen, texto o placeholder */}
                  {imageField && (
                    isImageUrl(imgSrc) ? (
                      <img
                        src={imgSrc}
                        alt={titleVal || ''}
                        style={{ width: '100%', height: 160, objectFit: 'cover', display: 'block', flexShrink: 0 }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <div className={classes.cardImagePlaceholder}
                        style={{ background: gradients[index % gradients.length] }}
                      >
                        {imgSrc ? (
                          <Typography style={{ color: '#fff', fontWeight: 600, fontSize: 15, textAlign: 'center', padding: '0 14px', lineHeight: 1.3 }}>
                            {imgSrc}
                          </Typography>
                        ) : (
                          <Icons.Image style={{ fontSize: 36, color: 'rgba(255,255,255,0.4)' }} />
                        )}
                      </div>
                    )
                  )}

                  {/* Cuerpo */}
                  <div className={classes.cardBody}>
                    {titleVal && (
                      <Typography style={{ fontWeight: 700, fontSize: 14, marginBottom: 6, lineHeight: 1.35,
                        overflow: 'hidden', display: '-webkit-box',
                        WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                      }}>
                        {titleVal}
                      </Typography>
                    )}
                    {descVal && (
                      <Typography variant="caption" style={{ color: '#757575', lineHeight: 1.5, flexGrow: 1,
                        display: '-webkit-box', overflow: 'hidden',
                        WebkitLineClamp: 3, WebkitBoxOrient: 'vertical',
                      }}>
                        {String(descVal).substring(0, 150)}
                        {String(descVal).length > 150 ? '…' : ''}
                      </Typography>
                    )}

                    {(badgeVal || personVal) && (
                      <div className={classes.cardMeta}>
                        {badgeVal && (
                          <Chip label={badgeVal} size="small" className={classes.cardBadge} />
                        )}
                        {personVal && (
                          <Typography variant="caption" style={{ color: '#9e9e9e', display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Icons.Person style={{ fontSize: 12 }} />
                            {personVal}
                          </Typography>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Footer con botón */}
                  {urlField && (
                    <div className={classes.cardFooter}>
                      <Typography variant="caption" style={{ color: '#7b1fa2', fontWeight: 700 }}>
                        {btnLabel} →
                      </Typography>
                    </div>
                  )}

                  {/* Badge de índice */}
                  <div className={classes.cardIndexBadge}>#{index + 1}</div>

                  {/* Controles superpuestos */}
                  <div className={classes.cardOverlay}>
                    <Tooltip title="Subir" enterDelay={400}>
                      <span>
                        <IconButton size="small" onClick={() => handleMoveItem(section.id, index, -1)} disabled={index === 0}>
                          <Icons.KeyboardArrowUp style={{ fontSize: 14 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Bajar" enterDelay={400}>
                      <span>
                        <IconButton size="small" onClick={() => handleMoveItem(section.id, index, 1)} disabled={index === items.length - 1}>
                          <Icons.KeyboardArrowDown style={{ fontSize: 14 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Editar" enterDelay={400}>
                      <IconButton size="small" onClick={() => handleOpenEditItem(section, index)} style={{ color: '#1976d2' }}>
                        <Icons.Edit style={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Eliminar" enterDelay={400}>
                      <IconButton size="small" onClick={() => handleDeleteItem(section.id, index)} style={{ color: '#f44336' }}>
                        <Icons.Delete style={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // ── Render sección lista ─────────────────────────────────────────────────

  const renderListSection = (section) => {
    const items = content[section.id] || [];
    const visibleFields = section.fields.slice(0, 3);

    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Icons.Add />}
            className={classes.addItemButton}
            onClick={() => handleOpenAddItem(section)}
          >
            Agregar elemento
          </Button>
        </div>

        {items.length === 0 ? (
          <div className={classes.noContent}>
            <Icons.Inbox style={{ fontSize: 40, opacity: 0.3 }} />
            <Typography variant="body2">Sin elementos. Agrega el primero.</Typography>
          </div>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell style={{ width: 40 }}>#</TableCell>
                {visibleFields.map((f) => (
                  <TableCell key={f.key}>{f.label}</TableCell>
                ))}
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item, index) => (
                <TableRow key={index} className={classes.tableRow}>
                  <TableCell>{index + 1}</TableCell>
                  {visibleFields.map((f) => (
                    <TableCell key={f.key}>
                      {f.type === 'image' && item[f.key] ? (
                        <img
                          src={item[f.key]}
                          alt={f.label}
                          style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }}
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      ) : f.type === 'boolean' ? (
                        <Chip
                          label={item[f.key] ? 'Sí' : 'No'}
                          size="small"
                          style={{
                            backgroundColor: item[f.key] ? '#e8f5e9' : '#fce4ec',
                            color: item[f.key] ? '#2e7d32' : '#c62828',
                          }}
                        />
                      ) : (
                        <Typography variant="body2" noWrap style={{ maxWidth: 200 }}>
                          {String(item[f.key] || '—').substring(0, 60)}
                          {String(item[f.key] || '').length > 60 ? '…' : ''}
                        </Typography>
                      )}
                    </TableCell>
                  ))}
                  <TableCell align="center">
                    <Tooltip title="Subir" enterDelay={400}>
                      <span>
                        <IconButton size="small" onClick={() => handleMoveItem(section.id, index, -1)} disabled={index === 0}>
                          <Icons.KeyboardArrowUp fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Bajar" enterDelay={400}>
                      <span>
                        <IconButton size="small" onClick={() => handleMoveItem(section.id, index, 1)} disabled={index === items.length - 1}>
                          <Icons.KeyboardArrowDown fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Editar" enterDelay={400}>
                      <IconButton size="small" onClick={() => handleOpenEditItem(section, index)} style={{ color: '#1976d2' }}>
                        <Icons.Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Eliminar" enterDelay={400}>
                      <IconButton size="small" onClick={() => handleDeleteItem(section.id, index)} style={{ color: '#f44336' }}>
                        <Icons.Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    );
  };

  // ── Render principal ─────────────────────────────────────────────────────

  const hasPages = pages.length > 0;

  return (
    <Grid container spacing={2} className={classes.root} justifyContent="center">
      <Grid item xs={12} sm={12} md={11} lg={10}>
        <Paper>
          {/* Toolbar */}
          <div className={classes.toolbar}>
            <Tooltip title="Volver a Diseños" enterDelay={400}>
              <IconButton onClick={() => history.push('/designs')}>
                <Icons.ArrowBack />
              </IconButton>
            </Tooltip>
            <div className={classes.toolbarTitle}>
              <Typography variant="h6" style={{ lineHeight: 1.1 }}>
                Gestión de Contenido
              </Typography>
              <Typography variant="caption" style={{ color: '#757575' }}>
                {designName}{currentPageTitle ? ` — ${currentPageTitle}` : ''}
              </Typography>
            </div>
            {hasPages && currentPageId && (
              <>
                <Tooltip title="Fondo de página" enterDelay={300}>
                  <Button
                    size="small"
                    startIcon={<Icons.FormatColorFill style={{ fontSize: 16 }} />}
                    onClick={() => setOpenPageStyle((v) => !v)}
                    style={{
                      borderRadius: 20,
                      padding: '4px 14px',
                      textTransform: 'none',
                      fontWeight: 600,
                      borderColor: openPageStyle ? '#9c27b0' : '#e0e0e0',
                      color: openPageStyle ? '#9c27b0' : '#757575',
                      border: '1px solid',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    Fondo
                    {pageStyle.backgroundColor && (
                      <span style={{
                        display: 'inline-block', width: 12, height: 12,
                        backgroundColor: pageStyle.backgroundColor,
                        borderRadius: '50%', border: '1px solid rgba(0,0,0,0.15)',
                        marginLeft: 4,
                      }} />
                    )}
                  </Button>
                </Tooltip>
                <Button
                  variant={previewMode ? 'contained' : 'outlined'}
                  startIcon={previewMode ? <Icons.Edit /> : <Icons.Visibility />}
                  onClick={() => setPreviewMode((v) => !v)}
                  className={classes.modeToggleBtn}
                  style={previewMode
                    ? { backgroundColor: '#9c27b0', color: '#fff', borderColor: '#9c27b0' }
                    : { borderColor: '#9c27b0', color: '#9c27b0' }
                  }
                >
                  {previewMode ? 'Editar' : 'Vista previa'}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <Icons.Save />}
                  onClick={handleSave}
                  disabled={isSaving}
                >
                  Guardar
                </Button>
              </>
            )}
          </div>

          {/* Panel fondo de página */}
          {openPageStyle && (
            <div style={{
              padding: '12px 20px', backgroundColor: '#fafafa',
              borderBottom: '1px solid #e0e0e0',
              display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 24,
            }}>
              <div>
                <Typography variant="caption" style={{ color: '#757575', display: 'block', marginBottom: 6, fontWeight: 600 }}>
                  Color de fondo
                </Typography>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="color"
                    value={pageStyle.backgroundColor || '#ffffff'}
                    onChange={(e) => setPageStyle((p) => ({ ...p, backgroundColor: e.target.value }))}
                    style={{ width: 36, height: 36, border: '1px solid #e0e0e0', cursor: 'pointer', borderRadius: 4, padding: 2 }}
                  />
                  <TextField
                    size="small"
                    value={pageStyle.backgroundColor || ''}
                    onChange={(e) => setPageStyle((p) => ({ ...p, backgroundColor: e.target.value }))}
                    placeholder="#ffffff"
                    inputProps={{ style: { fontFamily: 'monospace', fontSize: 12, width: 90 } }}
                    style={{ width: 110 }}
                  />
                  {pageStyle.backgroundColor && (
                    <IconButton size="small" onClick={() => setPageStyle((p) => ({ ...p, backgroundColor: '' }))} style={{ color: '#9e9e9e' }}>
                      <Icons.Close fontSize="small" />
                    </IconButton>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                  {['#ffffff','#f5f5f5','#f8f4ff','#e8f5e9','#e3f2fd','#fff8e1','#fce4ec','#212121','#1a1a2e'].map((c) => (
                    <div
                      key={c}
                      onClick={() => setPageStyle((p) => ({ ...p, backgroundColor: c }))}
                      style={{
                        width: 24, height: 24, borderRadius: 4, backgroundColor: c, cursor: 'pointer',
                        border: pageStyle.backgroundColor === c ? '2px solid #9c27b0' : '1px solid #e0e0e0',
                      }}
                    />
                  ))}
                </div>
              </div>
              <div style={{ flexGrow: 1, minWidth: 220 }}>
                <Typography variant="caption" style={{ color: '#757575', display: 'block', marginBottom: 6, fontWeight: 600 }}>
                  Imagen de fondo (URL)
                </Typography>
                <TextField
                  size="small"
                  fullWidth
                  value={pageStyle.backgroundImage || ''}
                  onChange={(e) => setPageStyle((p) => ({ ...p, backgroundImage: e.target.value }))}
                  placeholder="https://ejemplo.com/fondo.jpg"
                  InputProps={{
                    endAdornment: pageStyle.backgroundImage ? (
                      <IconButton size="small" onClick={() => setPageStyle((p) => ({ ...p, backgroundImage: '' }))} style={{ color: '#9e9e9e' }}>
                        <Icons.Close style={{ fontSize: 14 }} />
                      </IconButton>
                    ) : null,
                  }}
                />
              </div>
            </div>
          )}

          {/* Cuerpo */}
          {isLoadingInfo || isListing ? (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <CircularProgress />
              <Typography variant="body2" style={{ marginTop: 12, color: '#9e9e9e' }}>
                Cargando...
              </Typography>
            </div>
          ) : !hasPages ? (
            <div style={{ textAlign: 'center', padding: theme => theme.spacing(6) }}>
              <Alert severity="info" style={{ margin: 24 }}>
                No hay páginas con diseño guiado en este diseño.
                Ve al <strong>Diseño Guiado</strong> para crear y configurar las secciones primero.
              </Alert>
              <Button
                variant="outlined"
                startIcon={<Icons.Dashboard />}
                onClick={() => history.push(`/designer/guided/${designId}`)}
              >
                Ir al Diseñador Guiado
              </Button>
            </div>
          ) : (
            <div style={{ padding: '0 16px 24px' }}>
              {/* Tabs de páginas */}
              {pages.length > 1 && (
                <Tabs
                  value={tabIndex}
                  onChange={handleTabChange}
                  className={classes.pageTabsBar}
                  indicatorColor="primary"
                  textColor="primary"
                  variant="scrollable"
                >
                  {pages.map((p) => (
                    <Tab key={p.pageId} label={p.title} />
                  ))}
                </Tabs>
              )}

              {isLoadingPage ? (
                <div style={{ textAlign: 'center', padding: 32 }}>
                  <CircularProgress size={28} />
                </div>
              ) : sections.length === 0 ? (
                <Alert severity="info" style={{ margin: '24px 0' }}>
                  Esta página no tiene secciones configuradas.
                  Ve al <strong>Diseño Guiado</strong> para agregar secciones.
                </Alert>
              ) : previewMode ? (
                <>
                  {renderPreviewCanvas()}
                  <div style={{ textAlign: 'right', marginTop: 16 }}>
                    <Button variant="contained" color="primary"
                      startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <Icons.Save />}
                      onClick={handleSave} disabled={isSaving}>
                      Guardar contenido
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <Typography variant="body2" style={{ color: '#757575', margin: '12px 0' }}>
                    Completa el contenido de cada sección. Los cambios se guardan al presionar "Guardar".
                  </Typography>

                  {sections.map((section) => {
                    const sType = getSectionType(section.type);
                    const itemCount = section.isList
                      ? (content[section.id] || []).length
                      : null;

                    return (
                      <Accordion
                        key={section.id}
                        defaultExpanded
                        className={classes.sectionAccordion}
                        elevation={0}
                      >
                        <AccordionSummary
                          expandIcon={<Icons.ExpandMore />}
                          className={classes.sectionSummary}
                        >
                          {sType && (
                            <Icon style={{ color: '#7b1fa2', fontSize: 20 }}>
                              {sType.icon}
                            </Icon>
                          )}
                          <Typography style={{ fontWeight: 600, flexGrow: 1 }}>
                            {section.label}
                          </Typography>
                          {sType?.isTypography && (
                            <Chip
                              label="Tipografía"
                              size="small"
                              style={{
                                backgroundColor: '#fff3e0', color: '#e65100',
                                fontSize: 10, height: 18, marginRight: 4,
                              }}
                            />
                          )}
                          {sType?.isContainer && (
                            <Chip
                              label="Contenedor"
                              size="small"
                              style={{
                                backgroundColor: '#e8f5e9', color: '#1b5e20',
                                fontSize: 10, height: 18, marginRight: 4,
                              }}
                            />
                          )}
                          {!sType?.isTypography && (
                            <Chip
                              label={section.isList ? 'Lista' : 'Único'}
                              size="small"
                              className={section.isList ? classes.listChip : classes.uniqueChip}
                              style={{ marginRight: 8 }}
                            />
                          )}
                          {section.isList && (
                            <Typography variant="caption" style={{ color: '#9e9e9e', marginRight: 8 }}>
                              {itemCount} elemento{itemCount !== 1 ? 's' : ''}
                            </Typography>
                          )}
                        </AccordionSummary>
                        <AccordionDetails style={{ display: 'block' }}>
                          <Divider style={{ marginBottom: 12 }} />
                          {section.isList
                            ? (sType?.isContainer
                                ? renderCardPreviewSection(section, sType)
                                : renderListSection(section))
                            : renderUniqueSection(section)}
                          {(section.isList) && (
                            <div style={{ marginTop: 12 }}>
                              <Button size="small"
                                startIcon={<Icons.Palette style={{ fontSize: 14 }} />}
                                onClick={() => setOpenStylesFor((prev) => prev === section.id ? null : section.id)}
                                style={{ color: '#7b1fa2', textTransform: 'none', fontSize: 12 }}
                              >
                                {openStylesFor === section.id ? 'Ocultar apariencia' : 'Apariencia de cards'}
                              </Button>
                              {openStylesFor === section.id && renderStylePanel(section)}
                            </div>
                          )}
                        </AccordionDetails>
                      </Accordion>
                    );
                  })}

                  <div style={{ textAlign: 'right', marginTop: 16 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <Icons.Save />}
                      onClick={handleSave}
                      disabled={isSaving}
                    >
                      Guardar contenido
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </Paper>
      </Grid>

      {/* ── Dialog: Agregar/Editar item de lista ──────────────────────────── */}
      {(() => {
        const dialogSType   = itemDialogSection ? getSectionType(itemDialogSection?.type) : null;
        const isContainerDlg = dialogSType?.isContainer && itemDialogSection?.isList;

        // Mapeo de campos para preview en vivo
        const dlgFields    = itemDialogSection?.fields || [];
        const dlgImageF    = dlgFields.find((f) => f.type === 'image');
        const dlgTitleF    = dlgFields.find((f) => ['titulo', 'nombre'].includes(f.key))
                          || dlgFields.find((f) => f.type === 'text' && f.required)
                          || dlgFields.find((f) => f.type === 'text');
        const dlgDescF     = dlgFields.find((f) => ['descripcion', 'resumen', 'texto', 'respuesta'].includes(f.key) && f !== dlgTitleF)
                          || dlgFields.find((f) => (f.type === 'richtext' || f.type === 'text') && f !== dlgTitleF);
        const dlgBadgeF    = dlgFields.find((f) => ['badge', 'categoria', 'precio'].includes(f.key));
        const dlgPersonF   = dlgFields.find((f) => ['persona', 'autor', 'canalizador', 'cargo'].includes(f.key));
        const dlgUrlF      = dlgFields.find((f) => f.type === 'url');
        const dlgBtnTxtF   = dlgFields.find((f) => f.key === 'texto_boton');

        const previewImgSrc  = dlgImageF ? itemDialogData[dlgImageF.key]   : null;
        const previewTitle   = dlgTitleF ? itemDialogData[dlgTitleF.key]   : null;
        const previewDesc    = dlgDescF  ? itemDialogData[dlgDescF.key]    : null;
        const previewBadge   = dlgBadgeF ? itemDialogData[dlgBadgeF.key]   : null;
        const previewPerson  = dlgPersonF ? itemDialogData[dlgPersonF.key] : null;
        const previewBtnTxt  = (dlgBtnTxtF && itemDialogData[dlgBtnTxtF.key]) || 'Ver más';

        return (
          <Dialog
            open={openItemDialog}
            onClose={() => setOpenItemDialog(false)}
            fullWidth
            maxWidth={isContainerDlg ? 'md' : 'sm'}
            className={classes.itemDialog}
          >
            <DialogTitle>
              {itemDialogIndex === null ? 'Agregar elemento' : 'Editar elemento'}
              {itemDialogSection && (
                <Typography variant="caption" display="block" style={{ color: '#9e9e9e' }}>
                  {itemDialogSection.label}
                </Typography>
              )}
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={2}>
                {/* Formulario */}
                <Grid item xs={12} sm={isContainerDlg ? 7 : 12}>
                  <Grid container spacing={2}>

                    {/* ── Selector de página de detalle ── */}
                    {dialogSType?.canBelist && (() => {
                      const available = detailPageOptions.filter((p) => p.pageId !== currentPageId);
                      if (available.length === 0) return null;
                      const detailSlug = detailPageOptions.find((p) => p.pageId === selectedDetailPageId)?.slug || '';
                      const slugOptions = detailItems.filter((it) => it.slug);
                      return (
                        <>
                          <Grid item xs={12}>
                            <Typography variant="caption" style={{ fontWeight: 600 }} display="block">
                              Página de detalle
                              <span style={{ color: '#9e9e9e', marginLeft: 4, fontWeight: 400 }}>(vincula el slug)</span>
                            </Typography>
                            <FormControl fullWidth size="small">
                              <Select
                                value={selectedDetailPageId}
                                onChange={(e) => {
                                  const pid = e.target.value;
                                  setSelectedDetailPageId(pid);
                                  setDetailItems([]);
                                  if (pid) loadDetailItemsForPage(pid);
                                }}
                                displayEmpty
                                disabled={loadingDetail}
                              >
                                <MenuItem value=""><em>Sin página de detalle</em></MenuItem>
                                {available.map((p) => (
                                  <MenuItem key={p.pageId} value={p.pageId}>{p.title}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>

                          {selectedDetailPageId && (
                            <>
                              <Grid item xs={12} sm={6}>
                                <Typography variant="caption" style={{ fontWeight: 600 }} display="block">
                                  Elemento de detalle
                                  <span style={{ color: '#f44336', marginLeft: 2 }}>*</span>
                                </Typography>
                                {loadingDetail ? (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0' }}>
                                    <CircularProgress size={16} />
                                    <Typography variant="caption" style={{ color: '#757575' }}>Cargando...</Typography>
                                  </div>
                                ) : slugOptions.length > 0 ? (
                                  <FormControl fullWidth size="small">
                                    <Select
                                      value={itemDialogData.slug || ''}
                                      onChange={(e) => {
                                        const chosen = slugOptions.find((it) => it.slug === e.target.value);
                                        setItemDialogData((prev) => ({
                                          ...prev,
                                          slug: e.target.value,
                                          ...(chosen?.titulo && !prev.titulo ? { titulo: chosen.titulo } : {}),
                                          ...(chosen?.nombre && !prev.nombre ? { nombre: chosen.nombre } : {}),
                                          ...(chosen?.imagen && !prev.imagen ? { imagen: chosen.imagen } : {}),
                                        }));
                                      }}
                                      displayEmpty
                                    >
                                      <MenuItem value=""><em>Seleccionar elemento...</em></MenuItem>
                                      {slugOptions.map((it) => (
                                        <MenuItem key={it.slug} value={it.slug}>
                                          {it.titulo || it.nombre || it.slug}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                ) : (
                                  <Typography variant="caption" style={{ color: '#e65100', display: 'block', marginTop: 4 }}>
                                    La página de detalle no tiene elementos. Agrégalos primero en esa página.
                                  </Typography>
                                )}
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <Typography variant="caption" style={{ fontWeight: 600 }} display="block">
                                  Enlace
                                  <span style={{ color: '#9e9e9e', marginLeft: 4, fontWeight: 400 }}>(automático)</span>
                                </Typography>
                                <div style={{ padding: '6px 10px', border: '1px solid #e0e0e0', borderRadius: 4, backgroundColor: '#f5f5f5', fontSize: 13, color: '#555', fontFamily: 'monospace', minHeight: 34 }}>
                                  {itemDialogData.slug && detailSlug
                                    ? `/${detailSlug}/${itemDialogData.slug}`
                                    : <span style={{ color: '#bdbdbd' }}>Se completa al seleccionar</span>
                                  }
                                </div>
                              </Grid>
                            </>
                          )}
                        </>
                      );
                    })()}

                    {dlgFields.map((field) => {
                      // Omitir slug y url cuando hay página de detalle activa
                      if (selectedDetailPageId && (field.key === 'slug' || field.key === 'url')) return null;
                      return (
                      <Grid item xs={12} sm={field.type === 'richtext' ? 12 : 6} key={field.key}>
                        <Typography variant="caption" style={{ fontWeight: 600 }} display="block">
                          {field.label}
                          {field.required && <span style={{ color: '#f44336', marginLeft: 2 }}>*</span>}
                        </Typography>
                        <FieldInput
                          field={field}
                          value={itemDialogData[field.key]}
                          isContainer={isContainerDlg}
                          onChange={(val) =>
                            setItemDialogData((prev) => ({ ...prev, [field.key]: val }))
                          }
                        />
                      </Grid>
                      );
                    })}
                  </Grid>
                </Grid>

                {/* Preview en vivo (solo para containers) */}
                {isContainerDlg && (
                  <Grid item xs={12} sm={5}>
                    <Typography variant="caption" style={{ color: '#9e9e9e', fontWeight: 600, display: 'block', marginBottom: 8 }}>
                      Vista previa
                    </Typography>
                    <div style={{
                      border: '1px solid #e0e0e0', borderRadius: 8, overflow: 'hidden',
                      background: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                      display: 'flex', flexDirection: 'column',
                    }}>
                      {/* Image / texto / placeholder */}
                      {dlgImageF && (
                        isImageUrl(previewImgSrc) ? (
                          <img
                            src={previewImgSrc}
                            alt=""
                            style={{ width: '100%', height: 140, objectFit: 'cover', display: 'block' }}
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        ) : (
                          <div style={{
                            width: '100%', height: 140,
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}>
                            {previewImgSrc ? (
                              <Typography style={{ color: '#fff', fontWeight: 600, fontSize: 14, textAlign: 'center', padding: '0 12px', lineHeight: 1.3 }}>
                                {previewImgSrc}
                              </Typography>
                            ) : (
                              <Icons.Image style={{ fontSize: 32, color: 'rgba(255,255,255,0.4)' }} />
                            )}
                          </div>
                        )
                      )}
                      {/* Content */}
                      <div style={{ padding: '12px 14px', flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                        {previewTitle ? (
                          <Typography style={{ fontWeight: 700, fontSize: 13, marginBottom: 6, lineHeight: 1.35 }}>
                            {previewTitle}
                          </Typography>
                        ) : (
                          <div style={{ height: 16, background: '#f0f0f0', borderRadius: 3, marginBottom: 6, width: '70%' }} />
                        )}
                        {previewDesc ? (
                          <Typography variant="caption" style={{ color: '#757575', lineHeight: 1.5, display: 'block', marginBottom: 8 }}>
                            {String(previewDesc).substring(0, 100)}
                            {String(previewDesc).length > 100 ? '…' : ''}
                          </Typography>
                        ) : (
                          <div style={{ marginBottom: 8 }}>
                            <div style={{ height: 10, background: '#f5f5f5', borderRadius: 3, marginBottom: 4 }} />
                            <div style={{ height: 10, background: '#f5f5f5', borderRadius: 3, width: '80%' }} />
                          </div>
                        )}
                        {(previewBadge || previewPerson) && (
                          <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 'auto', flexWrap: 'wrap' }}>
                            {previewBadge && (
                              <Chip label={previewBadge} size="small" className={classes.cardBadge} />
                            )}
                            {previewPerson && (
                              <Typography variant="caption" style={{ color: '#9e9e9e', display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Icons.Person style={{ fontSize: 11 }} />
                                {previewPerson}
                              </Typography>
                            )}
                          </div>
                        )}
                      </div>
                      {/* Footer */}
                      {dlgUrlF && (
                        <div style={{ borderTop: '1px solid #f0f0f0', padding: '8px 14px', textAlign: 'center' }}>
                          <Typography variant="caption" style={{ color: '#7b1fa2', fontWeight: 700 }}>
                            {previewBtnTxt} →
                          </Typography>
                        </div>
                      )}
                    </div>
                  </Grid>
                )}
              </Grid>

              {itemDialogError && (
                <Alert severity="error" style={{ marginTop: 12 }}>{itemDialogError}</Alert>
              )}
            </DialogContent>
            <DialogActions style={{ padding: '10px 24px' }}>
              <Button onClick={() => setOpenItemDialog(false)}>Cancelar</Button>
              <Button variant="contained" color="primary" onClick={handleConfirmItem}>
                {itemDialogIndex === null ? 'Agregar' : 'Guardar cambios'}
              </Button>
            </DialogActions>
          </Dialog>
        );
      })()}

      {/* ── Toast ─────────────────────────────────────────────────────────── */}
      <Snackbar open={openToast} autoHideDuration={5000} onClose={() => setOpenToast(false)}>
        <Alert onClose={() => setOpenToast(false)} severity={toastSeverity}>
          {toastMsg}
        </Alert>
      </Snackbar>
    </Grid>
  );
}

export default ContentManagerPage;
